/**
 * @file Shared React component — components/Layout/SidebarAdmin.tsx.
 */

import React, { useState, useEffect } from "react";
import "./Sidebar.css";
import axiosClient from "../../api/axiosClient";
import SidebarFooter from "./SidebarFooter";

interface SidebarAdminProps {
  onSelect: (section: string) => void;
  activeSection: string;
}

const SidebarAdmin: React.FC<SidebarAdminProps> = ({ onSelect, activeSection }) => {
  const [active, setActive] = useState(activeSection || "Home");
  const [materials, setMaterials] = useState<any[]>([]);
  const [materialsOpen, setMaterialsOpen] = useState(false);

  // 🔹 Fetch materials from backend
  useEffect(() => {
    axiosClient.get("/materials").then((res) => {
      if (res.data.success) {
        setMaterials(res.data.data);
      }
    });
  }, []);

  // Keep sidebar highlight in sync with dashboard navigation
  useEffect(() => {
    setActive(activeSection);
    if (activeSection.startsWith("material_")) {
      setMaterialsOpen(true);
    }
  }, [activeSection]);

  const handleClick = (section: string) => {
    setActive(section);
    onSelect(section);

    // Auto-expand if selecting a material
    if (section.startsWith("material_")) {
      setMaterialsOpen(true);
    }
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-inner">
          {/* ============================
              TOP STATIC ITEMS
          ============================== */}
          <button
            className={`menu-btn ${active === "Home" ? "active" : ""}`}
            onClick={() => handleClick("Home")}
          >
            Home
          </button>

          <button
            className={`menu-btn ${active === "Profile" ? "active" : ""}`}
            onClick={() => handleClick("Profile")}
          >
            Profile
          </button>

          <button
            className={`menu-btn ${active === "Manage Users" ? "active" : ""}`}
            onClick={() => handleClick("Manage Users")}
          >
            Manage Users
          </button>

          <button
            className={`menu-btn ${active === "Manage Experts" ? "active" : ""}`}
            onClick={() => handleClick("Manage Experts")}
          >
            Manage Experts
          </button>

          {/* ============================
              Submissions Section
          ============================== */}
          <button
            className={`menu-btn ${active === "Submissions" ? "active" : ""}`}
            onClick={() => handleClick("Submissions")}
          >
            Submissions
          </button>

          {/* ============================
              MATERIALS OVERVIEW
          ============================== */}
          <div className="sidebar-group">
            {/* Main Expandable Header */}
            <div
              className={`sidebar-group-title ${materialsOpen ? "open" : ""}`}
              onClick={() => setMaterialsOpen(!materialsOpen)}
            >
              <span>Materials Overview</span>
              <span className="arrow">{materialsOpen ? "▼" : "▶"}</span>
            </div>

            {/* Submenu Items */}
            {materialsOpen && (
              <div className="sidebar-submenu">
                {materials.length === 0 && (
                  <div className="sidebar-subitem empty">No materials found</div>
                )}

                {materials.map((mat) => (
                  <div
                    key={mat.id}
                    className={`sidebar-subitem ${
                      active === `material_${mat.id}` ? "active" : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation(); // prevent closing parent
                      handleClick(`material_${mat.id}`);
                    }}
                  >
                    {mat.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ============================
              OTHER MENU ITEMS
          ============================== */}
          <button
            className={`menu-btn ${active === "Public Pages" ? "active" : ""}`}
            onClick={() => handleClick("Public Pages")}
          >
            Public Pages
          </button>

          <button
            className={`menu-btn ${active === "Events" ? "active" : ""}`}
            onClick={() => handleClick("Events")}
          >
            Events
          </button>

          <button
            className={`menu-btn ${
              active === "Reliability Standards" ? "active" : ""
            }`}
            onClick={() => handleClick("Reliability Standards")}
          >
            Reliability Standards
          </button>

          <button
            className={`menu-btn ${
              active === "Characterization factors" ? "active" : ""
            }`}
            onClick={() => handleClick("Characterization factors")}
          >
            Characterization factors
          </button>

          <button
            className={`menu-btn ${
              active === "Calorific values" ? "active" : ""
            }`}
            onClick={() => handleClick("Calorific values")}
          >
            Calorific values
          </button>

          <button
            className={`menu-btn ${
              active === "Fuel densities" ? "active" : ""
            }`}
            onClick={() => handleClick("Fuel densities")}
          >
            Fuel densities
          </button>

          <button
            className={`menu-btn ${
              active === "Manufacturing processes" ? "active" : ""
            }`}
            onClick={() => handleClick("Manufacturing processes")}
          >
            Manufacturing processes
          </button>

          {/* Keep these as the last menu items (not pinned) */}
          <button
            className={`menu-btn ${active === "Form" ? "active" : ""}`}
            onClick={() => handleClick("Form")}
          >
            Form
          </button>

          <button
            className={`menu-btn ${active === "Manage Dropdowns" ? "active" : ""}`}
            onClick={() => handleClick("Manage Dropdowns")}
          >
            Manage Dropdowns
          </button>

          <button
            className={`menu-btn ${active === "Cite This Tool" ? "active" : ""}`}
            onClick={() => handleClick("Cite This Tool")}
          >
            Cite This Tool
          </button>

          <div className="progress-line"></div>
      </div>

      {/* ============================
          FOOTER
      ============================== */}
      <SidebarFooter showCiteButton={false} />
    </aside>
  );
};

/** React component — Sidebar Admin. */
export default SidebarAdmin;